import { Menu } from "@mui/material";
import {
    type MouseEvent,
    type ReactNode,
    type Ref,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

interface CustomNavDropdownProps {
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
    ref?: Ref<CustomNavDropdownHandle>;
}

export interface CustomNavDropdownHandle {
    dropdownRef: {
        toggle: () => void;
        close: () => void;
        open: () => void;
    };
}

/**
 * Nav-bar item that opens its children in a MUI menu.
 *
 * @param root0 - The component props. `ref` exposes `dropdownRef` with
 *   `toggle()`, `open()`, and `close()`.
 * @returns The rendered nav dropdown.
 */
const CustomNavDropdown = ({
    title,
    children,
    className,
    ref,
}: CustomNavDropdownProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const containerRef = useRef<HTMLLIElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useImperativeHandle(ref, () => ({
        get dropdownRef() {
            return {
                toggle: () =>
                    setAnchorEl((prev) => (prev ? null : containerRef.current)),
                close: handleClose,
                open: () => setAnchorEl(containerRef.current),
            };
        },
    }));

    return (
        <li className="nav-item" ref={containerRef}>
            <span
                className="nav-link"
                onClick={handleOpen}
                style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    padding: "0 1rem",
                }}
            >
                {title}
            </span>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transitionDuration={0}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: -1,
                    horizontal: "left",
                }}
                slotProps={{
                    paper: {
                        className: className,
                        sx: { ml: 2 },
                    },
                }}
            >
                {children}
            </Menu>
        </li>
    );
};

export default CustomNavDropdown;
